// Contexte React des rôles employés configurables (table `roles_employe`).
//
// Seul le DG (propriétaire ou agence) charge et gère les rôles. L'isolation
// par organisation est garantie par la RLS Supabase ; le client ne fait que
// fournir l'identifiant de son organisation, vérifié côté serveur.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSession } from "@/session/SessionProvider";
import { describeSupabaseError } from "@/store/shared/dataErrors";
import type { PermissionMap } from "./catalog";
import {
  countEmployesForRole,
  createRoleEmploye,
  deleteRoleEmploye,
  fetchRolesEmploye,
  fetchScopeId,
  updateRoleEmploye,
  type OrganisationScope,
  type RoleEmploye,
} from "./rolesData";

interface RolesContextValue {
  roles: RoleEmploye[];
  loading: boolean;
  error: string | null;
  /** Vrai si l'utilisateur connecté peut gérer les rôles (DG actif). */
  canManage: boolean;
  reload: () => Promise<void>;
  createRole: (nom: string, permissions: PermissionMap) => Promise<void>;
  updateRole: (id: string, nom: string, permissions: PermissionMap) => Promise<void>;
  deleteRole: (id: string) => Promise<void>;
  /** Nombre d'employés utilisant un code de rôle (mesure serveur). */
  countEmployes: (code: string) => Promise<number>;
  getRole: (code: string) => RoleEmploye | undefined;
  roleOptions: { value: string; label: string }[];
  roleLabel: (code: string) => string;
}

const RolesContext = createContext<RolesContextValue | null>(null);

export function RolesProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [roles, setRoles] = useState<RoleEmploye[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scopeRef = useRef<OrganisationScope | null>(null);

  const canManage =
    session?.role === "dg_proprietaire" || session?.role === "dg_agence";

  const load = useCallback(async () => {
    if (!session || !canManage) {
      setRoles([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (!scopeRef.current) {
        scopeRef.current = await fetchScopeId(session.id, session.role);
      }
      setRoles(await fetchRolesEmploye());
    } catch (caught) {
      setError(describeSupabaseError(caught));
      console.error("[Rôles] chargement Supabase échoué", caught);
    } finally {
      setLoading(false);
    }
  }, [session, canManage]);

  useEffect(() => {
    void load();
  }, [load]);

  const ensureScope = useCallback(async (): Promise<OrganisationScope> => {
    if (scopeRef.current) return scopeRef.current;
    if (!session) throw new Error("Session absente");
    const scope = await fetchScopeId(session.id, session.role);
    scopeRef.current = scope;
    return scope;
  }, [session]);

  const createRole = useCallback(
    async (nom: string, permissions: PermissionMap) => {
      const scope = await ensureScope();
      await createRoleEmploye(scope, nom, permissions);
      await load();
    },
    [ensureScope, load]
  );

  const updateRole = useCallback(
    async (id: string, nom: string, permissions: PermissionMap) => {
      const existing = roles.find((role) => role.id === id);
      if (!existing) throw new Error("Rôle introuvable");
      await updateRoleEmploye(id, existing.code, nom, permissions, existing.permissions);
      await load();
    },
    [roles, load]
  );

  const deleteRole = useCallback(
    async (id: string) => {
      const existing = roles.find((role) => role.id === id);
      if (!existing) throw new Error("Rôle introuvable");
      await deleteRoleEmploye(id, existing.code);
      await load();
    },
    [roles, load]
  );

  const getRole = useCallback((code: string) => roles.find((role) => role.code === code), [roles]);

  const roleOptions = useMemo(
    () => roles.map((role) => ({ value: role.code, label: role.nom })),
    [roles]
  );

  const roleLabel = useCallback(
    (code: string) => roles.find((role) => role.code === code)?.nom ?? (code || "Rôle non défini"),
    [roles]
  );

  const value = useMemo(
    () => ({
      roles,
      loading,
      error,
      canManage,
      reload: load,
      createRole,
      updateRole,
      deleteRole,
      countEmployes: countEmployesForRole,
      getRole,
      roleOptions,
      roleLabel,
    }),
    [roles, loading, error, canManage, load, createRole, updateRole, deleteRole, getRole, roleOptions, roleLabel]
  );

  return <RolesContext.Provider value={value}>{children}</RolesContext.Provider>;
}

export function useRoles(): RolesContextValue {
  const context = useContext(RolesContext);
  if (!context) {
    throw new Error("useRoles doit être utilisé à l'intérieur de RolesProvider");
  }
  return context;
}